import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, FileText, Loader2, Upload, X } from 'lucide-react';
import { profileCard } from './ProfileUI';

const allowedTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const maxBytes = 5 * 1024 * 1024;

const ResumeUpload = ({ onUpload, isUploading }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (selected) => {
    setError('');
    setFile(null);
    if (!selected) return;
    if (!allowedTypes.includes(selected.type)) {
      setError('Upload a PDF, DOC, or DOCX file');
      return;
    }
    if (selected.size > maxBytes) {
      setError('Resume must be 5MB or smaller');
      return;
    }
    setFile(selected);
  };

  const handleFileChange = (event) => {
    validateFile(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    validateFile(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!file) {
      setError('Select a resume before continuing');
      return;
    }
    onUpload(file);
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={profileCard}
    >
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-300">AI import</p>
        <h2 className="mt-2 text-2xl font-bold text-white">Upload your resume</h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400">
          Drop your resume here and AIVION Learn will parse it, save the results, and open an editable profile review.
        </p>
      </div>

      <label
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`group flex min-h-72 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-5 text-center transition ${isDragging ? 'border-purple-300 bg-purple-500/20 shadow-lg shadow-purple-500/20' : 'border-slate-700/70 bg-slate-900/50 hover:border-purple-400/70 hover:bg-purple-500/10 hover:shadow-lg hover:shadow-purple-500/10'}`}
      >
        <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-xl shadow-purple-500/25 transition group-hover:-translate-y-1">
          {isUploading ? <Loader2 className="h-8 w-8 animate-spin" /> : <Upload className="h-8 w-8" />}
        </span>
        <span className="text-lg font-bold text-white">{file ? file.name : 'Drag and drop resume'}</span>
        <span className="mt-2 text-sm text-gray-400">PDF, DOC, or DOCX up to 5MB</span>
        <span className="mt-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-purple-500/20">
          Browse files
        </span>
        <input type="file" accept=".pdf,.doc,.docx" className="sr-only" onChange={handleFileChange} />
      </label>

      {file && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex flex-col gap-3 rounded-2xl border border-purple-400/30 bg-purple-500/20 p-4 shadow-lg shadow-purple-500/10 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <FileText className="h-5 w-5" />
            </span>
            <div>
              <p className="break-all text-sm font-semibold text-white">{file.name}</p>
              <p className="text-xs text-purple-100/80">{(file.size / (1024 * 1024)).toFixed(2)} MB ready to upload</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFile(null)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            <X className="h-4 w-4" />
            Remove
          </button>
        </motion.div>
      )}

      {isUploading && (
        <div className="mt-4 overflow-hidden rounded-full bg-white/10">
          <motion.div
            className="h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600"
            initial={{ width: '15%' }}
            animate={{ width: ['15%', '78%', '92%'] }}
            transition={{ duration: 1.4, repeat: Infinity, repeatType: 'reverse' }}
          />
        </div>
      )}

      {error && (
        <p className="mt-3 inline-flex items-center gap-2 rounded-xl border border-red-300/20 bg-red-400/10 px-3 py-2 text-sm font-medium text-red-100">
          <AlertCircle className="h-4 w-4" />
          {error}
        </p>
      )}

      {file && !error && !isUploading && (
        <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-purple-200">
          <CheckCircle2 className="h-4 w-4" />
          File passed validation
        </p>
      )}

      <button
        type="submit"
        disabled={isUploading}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-4 font-bold text-white shadow-2xl shadow-purple-500/20 transition hover:-translate-y-0.5 hover:from-purple-700 hover:to-pink-700 hover:shadow-pink-500/25 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isUploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
        {isUploading ? 'Parsing resume...' : 'Upload Resume'}
      </button>
    </motion.form>
  );
};

export default ResumeUpload;
